import sessionHelper from "./sessionHelper";

function Client({
  path = "",
  baseUrl = import.meta.env.VITE_CMS_API_URL,
  options = {},
  payload,
  isFormData = payload instanceof FormData, // Auto-detect FormData
  headers = {},
  redirect = "",
}: any) {
  const _url = (() => {
    let _path = path;

    if (_path[0] !== "/") _path = "/" + _path;

    return baseUrl + _path;
  })();

  let _headers: any = {
    Authorization: `Bearer ${sessionHelper?.token}`,
    ...headers,
  };

  // Only set Content-Type for non-FormData payloads
  if (!isFormData && !headers['Content-Type']) {
    _headers["Content-Type"] = "application/json";
  }

  const _options: any = {
    ...JSON.parse(JSON.stringify(options)),
    headers: _headers, // Use the headers we constructed
  };

  // Handle payload
  _options.body = isFormData ? payload : JSON.stringify(payload);

  if (redirect) _options.redirect = redirect;

  const _handleError = (error: any) => {
    console.error(error.message);
    throw error;
  };

  const _fetch = async (...args: any[]) => {
    try {
      const response = await fetch(...(args as [RequestInfo | URL, RequestInit?]));
      const responseJson = await response?.json();

      // Check for disabled account message
      if (responseJson?.message === "User account is disabled") {
        sessionHelper.onSignOut();
        window.location.href = "/signin";
        throw new Error("User account is disabled");
      }

      if (response.status === 401) {
        sessionHelper.onSignOut();
      }

      if (![200, 201].includes(response.status)) {
        // Check if details is an object (e.g., pack validation error with code and metadata)
        if (typeof responseJson?.details === 'object' && responseJson?.details !== null) {
          const error: any = new Error(responseJson?.message || "Operation failed");
          error.response = { data: { details: responseJson.details } };
          throw error;
        }

        // Otherwise, treat details as a string error message
        throw new Error(
          responseJson?.details ||
          responseJson?.message ||
          "Internal Server Error Occurred"
        );
      }

      return responseJson;
    } catch (error: any) {
      console.error("Fetch Error:", error.message); // Improved error message
      _handleError(error);
    }
  };

  /**
   * GET request to remote service
   */
  this.get = () => {
    // Add cache-busting parameter to prevent browser caching
    const cacheHeaders = {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    };

    return _fetch(_url, {
      ..._options,
      headers: {
        ..._options.headers,
        ...cacheHeaders
      },
      method: "GET",
    });
  };

  /**
   * POST request to remote service
   */
  this.post = () =>
    _fetch(_url, {
      ..._options,
      method: "POST",
    });

  /**
   * PUT request to remote service
   */
  this.put = () =>
    _fetch(_url, {
      ..._options,
      method: "PUT",
    });

  /**
   * PATCH request to remote service
   */
  this.patch = () =>
    _fetch(_url, {
      ..._options,
      method: "PATCH",
    });

  /**
   * DELETE request to remote service
   */
  this.delete = () =>
    _fetch(_url, {
      ..._options,
      method: "DELETE",
    });
}

export { Client };
